import { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../../../../../config/supabase';
import { validateUsername } from '../../../../../utils/accountValidation';

interface UserProfile {
  username: string;
}

interface UseUserProfileProps {
  user: User | null;
  isOpen: boolean;
  updateUsername: (username: string) => Promise<{ error: Error | null }>;
}

interface UseUserProfileReturn {
  userProfile: UserProfile | null;
  editingUsername: boolean;
  newUsername: string;
  usernameLoading: boolean;
  setEditingUsername: (editing: boolean) => void;
  setNewUsername: (username: string) => void;
  handleUpdateUsername: (
    checkingUsername: boolean,
    usernameAvailable: boolean | null
  ) => Promise<{ success: boolean; error?: string }>;
  cancelUsernameEdit: () => void;
}

export const useUserProfile = ({
  user,
  isOpen,
  updateUsername,
}: UseUserProfileProps): UseUserProfileReturn => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);

  // Fetch user profile when modal opens
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('username')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setUserProfile(data);
          setNewUsername(data.username || '');
        }
      }
    };

    if (isOpen && user) {
      fetchUserProfile();
    }
  }, [user, isOpen]);

  const handleUpdateUsername = useCallback(
    async (
      checkingUsername: boolean,
      usernameAvailable: boolean | null
    ): Promise<{ success: boolean; error?: string }> => {
      const validation = validateUsername(newUsername);
      if (!validation.isValid) {
        return { success: false, error: validation.error || 'Invalid username' };
      }

      if (checkingUsername) {
        return { success: false, error: 'Please wait while checking username availability' };
      }

      if (usernameAvailable === false) {
        return { success: false, error: 'Username already taken' };
      }

      setUsernameLoading(true);
      const { error } = await updateUsername(newUsername);
      setUsernameLoading(false);

      if (error) {
        return { success: false, error: error.message };
      }

      setUserProfile({ username: newUsername });
      setEditingUsername(false);
      return { success: true };
    },
    [newUsername, updateUsername]
  );

  const cancelUsernameEdit = useCallback(() => {
    setEditingUsername(false);
    setNewUsername(userProfile?.username || '');
  }, [userProfile]);

  return {
    userProfile,
    editingUsername,
    newUsername,
    usernameLoading,
    setEditingUsername,
    setNewUsername,
    handleUpdateUsername,
    cancelUsernameEdit,
  };
};
